import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { PromoBannerCarousel } from "@/components/PromoBanner";

function FiltersPanel({ filters, setFilters, onApply, categories, subTypes }) {
  const subs = subTypes[filters.category] || [];
  return (
    <div className="space-y-6">
      <div>
        <label className="text-sm font-semibold mb-2 block">Search</label>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Tour, destination, keyword..."
            className="pl-9"
            data-testid="filter-search-input"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold mb-2 block">Category</label>
        <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v, sub_category: "" }))}>
          <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.product_category || c.slug} value={c.product_category || c.slug}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {subs.length > 0 && (
        <div>
          <label className="text-sm font-semibold mb-2 block">Type</label>
          <div className="space-y-2">
            {subs.map((s) => (
              <label key={s} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={filters.sub_category === s}
                  onCheckedChange={(v) => setFilters((f) => ({ ...f, sub_category: v ? s : "" }))}
                  data-testid={`filter-sub-${s}`}
                />
                <span className="text-sm capitalize">{s.toLowerCase()}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-semibold mb-2 block">Price range (₹)</label>
        <Slider
          min={0}
          max={100000}
          step={500}
          value={[filters.min_price, filters.max_price]}
          onValueChange={(v) => setFilters((f) => ({ ...f, min_price: v[0], max_price: v[1] }))}
          data-testid="filter-price-slider"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>₹{filters.min_price.toLocaleString()}</span>
          <span>₹{filters.max_price.toLocaleString()}</span>
        </div>
      </div>

      <Button onClick={onApply} className="w-full bg-teal text-white hover:bg-teal-dark" data-testid="filter-apply">
        Apply Filters
      </Button>
    </div>
  );
}

export default function Search({ presetCategory, hideHeader = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    category: presetCategory || searchParams.get("category") || "",
    sub_category: searchParams.get("sub_category") || "",
    destination: searchParams.get("destination") || "",
    min_price: 0,
    max_price: 100000,
    sort: "popular",
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subTypes, setSubTypes] = useState({});

  // Sync filters from URL whenever searchParams change (e.g. clicking nav links)
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    const urlCat = presetCategory || searchParams.get("category") || "";
    const urlSub = searchParams.get("sub_category") || "";
    const urlDest = searchParams.get("destination") || "";
    setFilters((prev) => {
      if (prev.q === urlQ && prev.category === urlCat && prev.sub_category === urlSub && prev.destination === urlDest) return prev;
      return { ...prev, q: urlQ, category: urlCat, sub_category: urlSub, destination: urlDest };
    });
  }, [searchParams, presetCategory]);

  // Load categories from DB
  useEffect(() => {
    api.get("/categories").then((r) => {
      const all = (r.data || []).filter((c) => !c.parent_id);
      // Deduplicate by product_category
      const seen = new Set();
      const unique = all.filter((c) => {
        const pc = c.product_category || c.slug;
        if (seen.has(pc)) return false;
        seen.add(pc);
        return true;
      });
      setCategories(unique);
    }).catch(() => {});
  }, []);

  // Load sub-types when category changes
  useEffect(() => {
    if (filters.category && !subTypes[filters.category]) {
      api.get(`/categories/sub-types/${filters.category}`).then((r) => {
        setSubTypes((prev) => ({ ...prev, [filters.category]: r.data || [] }));
      }).catch(() => {});
    }
  }, [filters.category]);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.category) params.category = filters.category;
    if (filters.sub_category) params.sub_category = filters.sub_category;
    if (filters.destination) params.destination = filters.destination;
    if (filters.min_price > 0) params.min_price = filters.min_price;
    if (filters.max_price < 100000) params.max_price = filters.max_price;
    params.sort = filters.sort;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    fetchProducts();
    const sp = new URLSearchParams();
    if (filters.q) sp.set("q", filters.q);
    if (filters.category) sp.set("category", filters.category);
    if (filters.sub_category) sp.set("sub_category", filters.sub_category);
    if (filters.destination) sp.set("destination", filters.destination);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [filters.category, filters.sub_category, filters.sort]);

  const catLabel = categories.find((c) => (c.product_category || c.slug) === filters.category)?.name;
  const title = catLabel || "All Experiences";

  const sortSelect = (
    <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
      <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="popular">Popular</SelectItem>
        <SelectItem value="price_low">Price: Low to High</SelectItem>
        <SelectItem value="price_high">Price: High to Low</SelectItem>
        <SelectItem value="rating">Top Rated</SelectItem>
      </SelectContent>
    </Select>
  );

  const mobileFilters = (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="lg:hidden" data-testid="mobile-filters-trigger">
          <SlidersHorizontal className="w-4 h-4 mr-2" />Filters
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[320px] overflow-y-auto">
        <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
        <div className="mt-6">
          <FiltersPanel filters={filters} setFilters={setFilters} onApply={fetchProducts} categories={categories} subTypes={subTypes} />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="search-page">
      {!hideHeader && (
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl">{title}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {loading ? "Loading..." : `${products.length} options found`}
              {filters.destination && ` in ${filters.destination}`}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {mobileFilters}
            {sortSelect}
          </div>
        </div>
      )}
      {hideHeader && (
        <div className="flex justify-end mb-4">
          <div className="flex gap-2 items-center">
            {mobileFilters}
            {sortSelect}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden lg:block sticky top-24 self-start bg-white border border-border rounded-2xl p-6" data-testid="filters-sidebar">
          <FiltersPanel filters={filters} setFilters={setFilters} onApply={fetchProducts} categories={categories} subTypes={subTypes} />
        </aside>

        <div>
          <div className="mb-6">
            <PromoBannerCarousel position="search" />
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-border rounded-2xl overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-8 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="no-results">
              <X className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-display text-xl mb-1">No results</h3>
              <p className="text-muted-foreground text-sm">Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="search-results">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
